import axios from "axios";
import { APIClient } from "../helpers/api_helper";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";

const api = new APIClient();

const RESOURCE = "/professionals";

/** Linked user summary when the API eager-loads the relation. */
export interface ProfessionalUser {
  id: number;
  name: string;
  email: string;
}

export type MembershipAccessStatus = "none" | "pending" | "active";

/** Service names linked on the professional, for the team list. */
export interface ProfessionalEnabledService {
  id: number;
  name: string;
}

/** Weekly window already stored, as the salon wall clock (HH:MM). */
export interface ProfessionalScheduleSummarySlot {
  day_of_week: number;
  start_time: string;
  end_time: string;
  break_label?: string | null;
  work_label?: string | null;
}

/** Professional entity returned by GET /api/v1/professionals. */
export interface Professional {
  id: number;
  tenant_id: number;
  user_id: number | null;
  name: string;
  commission_rate_bps: number;
  weekly_commission_goal_cents?: number | null;
  weekly_appointments_goal?: number | null;
  monthly_commission_goal_cents?: number | null;
  monthly_appointments_goal?: number | null;
  color_hex: string;
  email?: string | null;
  country_code?: string | null;
  phone_number?: string | null;
  is_active?: boolean;
  archived_at?: string | null;
  archived_by_user_id?: number | null;
  photo_path?: string | null;
  hide_photo_in_online_booking?: boolean;
  /** Studio login access: none | pending invite | active membership. */
  access_status?: MembershipAccessStatus;
  /** ISO-8601 UTC timestamp of last invite (when access_status is pending). */
  invited_at?: string | null;
  created_at: string;
  updated_at: string;
  user?: ProfessionalUser | null;
  /** Marked services. The agenda ignores them while the salon option is off. */
  enabled_services?: ProfessionalEnabledService[];
  /** Saved weekly windows. Empty when hours are not set yet. */
  schedules?: ProfessionalScheduleSummarySlot[];
  /** Preview of per-service commission overrides (GAP-F2-086). */
  service_commission_overrides?: ProfessionalServiceCommissionOverride[];
  /** Total override count (may be greater than the preview list). */
  service_commission_overrides_total?: number;
}

/** Per-service commission override preview on the team list. */
export interface ProfessionalServiceCommissionOverride {
  service_id: number;
  service_name: string;
  commission_rate_bps: number;
}

/** Payload for POST /api/v1/professionals. */
export interface CreateProfessionalPayload {
  name: string;
  user_id?: number | null;
  commission_rate_bps?: number;
  weekly_commission_goal_cents?: number | null;
  weekly_appointments_goal?: number | null;
  monthly_commission_goal_cents?: number | null;
  monthly_appointments_goal?: number | null;
  color_hex: string;
  email?: string | null;
  country_code?: string | null;
  phone_number?: string | null;
}

/** Payload for PATCH /api/v1/professionals/{id} — all fields optional. */
export type UpdateProfessionalPayload = Partial<CreateProfessionalPayload>;

export interface ProfessionalListParams extends PaginationParams {
  search?: string;
  include_inactive?: boolean | 0 | 1;
  /** Only professionals with at least one per-service commission override. */
  has_service_commission_overrides?: boolean | 0 | 1;
}

export const getProfessionals = (
  params?: ProfessionalListParams
): Promise<PaginatedApiResponse<Professional>> =>
  api.get<PaginatedApiResponse<Professional>>(RESOURCE, params);

export const getProfessional = (id: number): Promise<ApiResponse<Professional>> =>
  api.get<ApiResponse<Professional>>(`${RESOURCE}/${id}`);

export const createProfessional = (
  payload: CreateProfessionalPayload
): Promise<ApiResponse<Professional>> =>
  api.create<ApiResponse<Professional>>(RESOURCE, payload);

export const updateProfessional = (
  id: number,
  payload: UpdateProfessionalPayload
): Promise<ApiResponse<Professional>> =>
  api.update<ApiResponse<Professional>>(`${RESOURCE}/${id}`, payload);

export const deleteProfessional = (
  id: number
): Promise<ApiResponse<Record<string, never>>> =>
  api.delete<ApiResponse<Record<string, never>>>(`${RESOURCE}/${id}`);

export const deactivateProfessional = (
  id: number
): Promise<ApiResponse<Professional>> =>
  api.update<ApiResponse<Professional>>(`${RESOURCE}/${id}/archive`, {});

export const restoreProfessional = (
  id: number
): Promise<ApiResponse<Professional>> =>
  api.update<ApiResponse<Professional>>(`${RESOURCE}/${id}/restore`, {});

/** Multipart portrait upload — crop 1:1 runs on the API. */
export const uploadProfessionalPhoto = (
  id: number,
  photo: File
): Promise<ApiResponse<Professional>> => {
  const formData = new FormData();
  formData.append("photo", photo);

  return axios.post(`${RESOURCE}/${id}/photo`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }) as Promise<ApiResponse<Professional>>;
};

export const deleteProfessionalPhoto = (
  id: number
): Promise<ApiResponse<Professional>> =>
  api.delete<ApiResponse<Professional>>(`${RESOURCE}/${id}/photo`);

/** Line item in GET /api/v1/professionals/{id}/commission-summary. */
export interface ProfessionalCommissionSummaryItem {
  order_item_id: number;
  order_id: number;
  paid_at: string | null;
  /** service | retail | package — GAP-F2-138 */
  item_type?: string;
  service_id?: number | null;
  service_name: string | null;
  product_id?: number | null;
  product_name?: string | null;
  line_total_cents: number;
  commission_rate_bps: number | null;
  /** True when the frozen rate differs from the professional's current default. */
  is_special_rate?: boolean;
  /** professional_default | service_override — from frozen rate vs current default. */
  rate_origin?: "professional_default" | "service_override" | string;
  commission_amount: number;
  /** accrued | paid_out — GAP-F2-127 */
  status?: "accrued" | "paid_out" | string;
  paid_out_at?: string | null;
  paid_out_by_user_id?: number | null;
  breakdown?: {
    base_cents: number;
    rate_bps: number | null;
    amount_cents: number;
    line_total_cents?: number;
    gross_commission_cents?: number;
    discount_share_cents?: number;
    cogs_share_cents?: number;
    fee_share_cents?: number;
  };
}

/** Commission summary for owner panel (GAP-F1-191 · ADJ-F2-123 · GAP-F2-127). */
export interface ProfessionalCommissionSummary {
  month: string;
  currency: string;
  professional_id: number;
  use_commission_tracking?: boolean;
  default_commission_rate_bps?: number;
  filters?: {
    service_id?: number | null;
    special_only?: boolean;
    /** frozen_ne_current_default = special when frozen ≠ current professional default */
    special_rule?: string;
    status?: "accrued" | "paid_out" | null;
  };
  summary: {
    total_commission_cents: number;
    accrued_cents?: number;
    paid_out_cents?: number;
    services_count: number;
    /** Sum of line commission_amount — must equal total_commission_cents (ADJ-F2-123). */
    lines_sum_cents?: number;
    totals_match?: boolean;
  };
  items: ProfessionalCommissionSummaryItem[];
}

export interface MarkCommissionsPaidOutResult {
  marked_count: number;
  skipped_count: number;
  marked_cents: number;
  order_item_ids: number[];
}

export interface UnmarkCommissionsPaidOutResult {
  unmarked_count: number;
  skipped_count: number;
  unmarked_cents: number;
  order_item_ids: number[];
}

export const getCommissionSummary = (
  id: number,
  month: string,
  filters?: {
    service_id?: number | null;
    special_only?: boolean;
    status?: "accrued" | "paid_out" | "all" | "";
  }
): Promise<ApiResponse<ProfessionalCommissionSummary>> =>
  api.get<ApiResponse<ProfessionalCommissionSummary>>(
    `${RESOURCE}/${id}/commission-summary`,
    {
      month,
      ...(filters?.service_id ? { service_id: filters.service_id } : {}),
      ...(filters?.special_only ? { special_only: 1 } : {}),
      ...(filters?.status && filters.status !== "all"
        ? { status: filters.status }
        : {}),
    }
  );

/** Month-close share text (GAP-F2-139) — layperson artifact, not CSV. */
export interface ProfessionalCommissionShareText {
  month: string;
  currency: string;
  use_commission_tracking?: boolean;
  text: string;
  line_count: number;
  truncated?: boolean;
  summary: {
    total_commission_cents: number;
    accrued_cents: number;
    paid_out_cents: number;
    lines_sum_cents: number;
    items_count: number;
    totals_match: boolean;
  };
}

export const getCommissionShareText = (
  id: number,
  month: string
): Promise<ApiResponse<ProfessionalCommissionShareText>> =>
  api.get<ApiResponse<ProfessionalCommissionShareText>>(
    `${RESOURCE}/${id}/commission-summary`,
    {
      month,
      format: "share_text",
    }
  );

export const downloadCommissionSummaryPdf = async (
  id: number,
  month: string
): Promise<Blob> => {
  const blob = await axios.get<Blob>(
    `${RESOURCE}/${id}/commission-summary/pdf`,
    {
      params: { month },
      responseType: "blob",
    }
  );

  return blob as unknown as Blob;
};

export const triggerCommissionPdfDownload = (
  blob: Blob,
  filename: string
): void => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const markCommissionsPaidOut = (
  id: number,
  body: {
    order_item_ids?: number[];
    month?: string;
    mark_all_accrued?: boolean;
  }
): Promise<ApiResponse<MarkCommissionsPaidOutResult>> =>
  api.create<ApiResponse<MarkCommissionsPaidOutResult>>(
    `${RESOURCE}/${id}/commissions/mark-paid-out`,
    body
  );

export const unmarkCommissionsPaidOut = (
  id: number,
  body: {
    order_item_ids?: number[];
    month?: string;
    unmark_all_paid_out?: boolean;
  }
): Promise<ApiResponse<UnmarkCommissionsPaidOutResult>> =>
  api.create<ApiResponse<UnmarkCommissionsPaidOutResult>>(
    `${RESOURCE}/${id}/commissions/unmark-paid-out`,
    body
  );

/** Period progress in GET /api/v1/professionals/{id}/goals (GAP-F2-068). */
export interface ProfessionalGoalsPeriod {
  start: string;
  end: string;
  key?: string;
  goals: {
    commission_cents: number | null;
    appointments: number | null;
  };
  progress: {
    commission_cents: number;
    appointments_completed: number;
  };
  percent: {
    commission: number | null;
    appointments: number | null;
  };
}

export interface ProfessionalGoalsSummary {
  currency: string;
  professional_id: number | null;
  timezone: string;
  week: ProfessionalGoalsPeriod;
  previous_week: ProfessionalGoalsPeriod;
  month: ProfessionalGoalsPeriod;
  week_comparison: {
    commission_cents_delta: number;
    commission_percent_delta: number | null;
    appointments_delta: number;
    appointments_percent_delta: number | null;
  };
  suggestions: {
    based_on_weeks: number;
    weekly_commission_goal_cents: number | null;
    weekly_appointments_goal: number | null;
    monthly_commission_goal_cents: number | null;
    monthly_appointments_goal: number | null;
  };
  near_goal: {
    threshold_percent: number;
    any: boolean;
    week: boolean;
    month: boolean;
    hint: string | null;
  };
}

export const getProfessionalGoals = (
  id: number
): Promise<ApiResponse<ProfessionalGoalsSummary>> =>
  api.get<ApiResponse<ProfessionalGoalsSummary>>(`${RESOURCE}/${id}/goals`);

/** Schedule block (absence / time off) returned by GET /api/v1/professionals/{id}/blocks. */
export interface ScheduleBlock {
  id: number;
  tenant_id: number;
  professional_id: number;
  start_datetime: string;
  end_datetime: string;
  reason?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduleBlockListParams extends PaginationParams {}

export interface CreateScheduleBlockPayload {
  start_datetime: string;
  end_datetime: string;
  reason?: string | null;
}

export type UpdateScheduleBlockPayload = Partial<CreateScheduleBlockPayload>;

export const getProfessionalBlocks = (
  professionalId: number,
  params?: ScheduleBlockListParams
): Promise<PaginatedApiResponse<ScheduleBlock>> =>
  api.get<PaginatedApiResponse<ScheduleBlock>>(
    `${RESOURCE}/${professionalId}/blocks`,
    params
  );

export const createProfessionalBlock = (
  professionalId: number,
  payload: CreateScheduleBlockPayload
): Promise<ApiResponse<ScheduleBlock>> =>
  api.create<ApiResponse<ScheduleBlock>>(
    `${RESOURCE}/${professionalId}/blocks`,
    payload
  );

export const deleteProfessionalBlock = (
  professionalId: number,
  blockId: number
): Promise<ApiResponse<Record<string, never>>> =>
  api.delete<ApiResponse<Record<string, never>>>(
    `${RESOURCE}/${professionalId}/blocks/${blockId}`
  );

export interface InviteProfessionalPayload {
  email: string;
}

export interface InviteProfessionalResult {
  user: ProfessionalUser;
  membership: {
    id: number;
    status: string;
    invited_at: string | null;
  };
  setup_required: boolean;
  invite_url?: string | null;
  professional: Professional;
}

export const inviteProfessional = (
  professionalId: number,
  payload: InviteProfessionalPayload
): Promise<ApiResponse<InviteProfessionalResult>> =>
  api.create<ApiResponse<InviteProfessionalResult>>(
    `${RESOURCE}/${professionalId}/invite`,
    payload
  );
